/**
 * Distribution Agreements — 11 documents.
 *
 * The highest-stakes contract most CPG founders sign. An exclusive territory with no performance
 * minimums and no clean exit can freeze a region for years and follows the brand into an exit.
 */
export const DISTRIBUTION_DOCS = [
  {
    id: "beverage-distributor-agreement",
    name: "Beverage Distributor Agreement",
    category: "distribution",
    subcategory: "Core distribution",
    aliases: [
      "Beverage Distribution Agreement",
      "Wholesaler Agreement",
      "Beverage Wholesaler Contract",
      "Distributor Appointment Agreement",
    ],
    stages: ["launch", "distribution", "growth"],
    importance: "essential",
    entityTypes: ["any"],
    parties: "Brand + Beverage Distributor (Wholesaler)",
    whenYouNeedIt:
      "Before you ship your first pallet to a beverage wholesaler, and before you let a distributor start calling on accounts in your name. If a wholesaler is already selling your product on a handshake and a price list, you need this now, not at renewal.",
    whatItDoes:
      "Appoints a wholesaler to buy your product and resell it into a defined set of accounts and geography. It sets whether that appointment is exclusive, what the distributor must sell to keep it, what you charge them, who pays for spoils, samples and promotions, and how either side gets out.",
    whyItMatters:
      "This is the single agreement most likely to cap the value of your brand. An exclusive territory with no performance minimums and no clean termination right can freeze a region for years while producing almost no sales, and you cannot appoint anyone else there while it runs. Beverage distribution has a long-standing reputation for being hard to exit, and how hard it actually is depends on the state and on exactly what your contract says — that is a question for counsel in the relevant state, before you sign. And it follows you: a buyer looking at your brand discounts for a distributor agreement they cannot get out of, because they inherit it.",
    majorTerms: [
      "Territory definition and whether it is exclusive, sole or non-exclusive",
      "Channels covered: off-premise, on-premise, club, convenience, e-commerce",
      "Annual and quarterly minimum purchase volumes",
      "Distribution targets by account count or points of distribution",
      "Price to distributor and distributor margin",
      "Free fill, samples and point-of-sale material supply",
      "Spoils, out-of-date and damaged product responsibility",
      "Promotional and depletion allowance funding",
      "Depletion and inventory reporting cadence",
      "Freight terms and who pays inbound",
      "Term, renewal and notice periods",
      "Termination for cause, for convenience and cure periods",
      "Inventory repurchase on termination",
      "Change of control, brand sale and assignment",
    ],
    clausesToWatch: [
      {
        clause: "Grant of exclusivity",
        why: "An exclusive grant with no volume or account-count condition attached hands away a whole region permanently in exchange for nothing enforceable.",
      },
      {
        clause: "Performance standards and minimum depletions",
        why: "If the minimums are aspirational rather than a stated condition of keeping the territory, missing them costs the distributor nothing and you have no lever.",
      },
      {
        clause: "Termination without cause",
        why: "Without a stated right to end the agreement on notice, your only exit is proving breach, which is slow, contested and expensive while the territory sits idle.",
      },
      {
        clause: "Cure periods",
        why: "A long cure window that resets on every notice lets a distributor stay just inside compliance indefinitely while never actually building the business.",
      },
      {
        clause: "Change of control and assignment",
        why: "Language letting the distributor consent to, block or be paid on a sale of your brand turns them into a party at your own exit negotiation.",
      },
      {
        clause: "Inventory repurchase on termination",
        why: "If you must buy back all remaining inventory at the distributor's cost plus freight and handling, terminating a badly performing distributor becomes a cash event you may not be able to fund.",
      },
      {
        clause: "Spoils, out-of-date and damaged product",
        why: "Open-ended credit for anything the distributor calls unsaleable turns your margin into a variable the other side controls.",
      },
      {
        clause: "E-commerce, Amazon and house account carve-outs",
        why: "If the grant covers all sales in the territory without carve-outs, your own website, Amazon listing and direct national accounts can end up owing the distributor a margin.",
      },
      {
        clause: "Automatic renewal",
        why: "A term that renews for another multi-year period unless you give notice far in advance quietly converts a trial into a long-term commitment.",
      },
    ],
    cpgIssues: [
      "Depletion reporting is the only view you have of real sell-through — without a contractual obligation to report depletions by account and SKU, you are managing the brand on shipment data that says nothing about whether anything sold.",
      "Out-of-date and spoiled product on a short-shelf-life beverage is a real, recurring cost; the agreement should define who inspects, what evidence is required and whether credit is capped as a percentage of purchases.",
      "Free fills to open a new account are standard practice in beverage but the volume is negotiable — an uncapped free-fill obligation on a growing account base is an unbudgeted line item.",
      "On-premise and off-premise are different businesses with different pricing, pack sizes and service expectations; a single undifferentiated grant lets a distributor take both and service only one.",
      "Cold-chain and warm-storage requirements matter for functional and refrigerated beverages — if the distributor's warehouse and trucks are not specified, product quality failures land back on you.",
      "Pallet configuration, case pack, code dating and lot coding need to match what the distributor's warehouse system and the retailer's receiving dock expect, or you will be paying receiving penalties you never priced in.",
      "Point-of-sale material, coolers and racks supplied by you can end up unaccounted for; specify ownership and return on termination.",
    ],
    founderMistakes: [
      "Granting an exclusive statewide territory to the first distributor who showed enthusiasm, with no minimums, then spending two years unable to sell into that state through anyone else.",
      "Treating the volume forecast in the appendix as a performance commitment when the operative clause never said missing it was a breach.",
      "Not carving out their own website, Amazon and direct-to-consumer sales, then receiving an invoice for distributor margin on their own e-commerce revenue.",
      "Accepting an inventory repurchase obligation without modelling it, then discovering the cost of terminating exceeded a quarter of revenue.",
      "Discovering during acquisition diligence that the distributor had a consent right over a change of control, and paying to make the problem go away out of the purchase price.",
      "Signing a distributor agreement while assuming the distributor was a broker, and only learning the difference when the distributor took title, set its own retail pricing and owned the retailer relationship.",
    ],
    negotiationPoints: [
      "Ask that exclusivity be expressly conditioned on stated annual volume and points-of-distribution targets, with automatic conversion to non-exclusive if they are missed.",
      "Ask for a termination-without-cause right on defined notice, mutual if necessary, so you are never dependent on proving breach.",
      "Carve out e-commerce, your own DTC site, Amazon, military and government accounts, and any national or house accounts you already service, by name.",
      "Cap the spoils and damaged-product credit as a stated percentage of purchases, with an inspection and evidence requirement.",
      "Limit inventory repurchase to product still within a stated remaining shelf life, in saleable condition, at the price the distributor paid, and net of amounts owed to you.",
      "Ask for depletion and inventory reporting by SKU and account at a stated cadence, with reporting failure treated as a curable breach.",
      "Make assignment and change of control mutual and consent-free for you, so a sale of your brand does not require their signature.",
      "Start with a shorter initial term and earn renewal on performance rather than accepting a long lock for better opening pricing.",
    ],
    redFlags: [
      "Perpetual or evergreen exclusivity with no performance condition of any kind.",
      "No termination right except for uncured material breach.",
      "A requirement that you repurchase all inventory at cost plus handling regardless of age or condition.",
      "Distributor consent required for a sale of your company, your brand or your assets.",
      "Silence on e-commerce and direct-to-consumer sales in a grant written to cover all sales in the territory.",
      "Uncapped spoils, damages and unsaleable credits taken by deduction rather than claim.",
      "The distributor's right to appoint sub-distributors without your approval.",
      "A price-increase mechanism that lets the distributor reject increases while holding the territory.",
    ],
    beforeYouSign: [
      "The distributor's actual account list and route coverage in the territory you are granting, not the map they showed you.",
      "A written volume and points-of-distribution plan for years one and two that you are willing to see written into the contract as a condition.",
      "Your own margin model with free fills, spoils allowance, promotional funding and freight loaded in, showing what you actually net per case.",
      "Confirmation of whether they carry a directly competing brand and what happens if they add one.",
      "Counsel in the relevant state, specifically on how easy or hard this agreement will be to end.",
    ],
    related: [
      "exclusive-distribution-agreement",
      "dsd-distributor-agreement",
      "non-exclusive-distribution-agreement",
      "territory-distribution-agreement",
      "notice-of-termination",
      "brand-acquisition-agreement",
    ],
    attorneyReview: "strongly-recommended",
    attorneyNote:
      "How hard a beverage distributor is to terminate varies by state and by contract language, and getting it wrong can lock a territory and reduce what a buyer will pay for the brand.",
    templateId: "beverage-distributor-agreement",
    templateVersion: "1.0",
    lastReviewed: "2026-08-25",
  },

  {
    id: "food-distributor-agreement",
    name: "Food Distributor Agreement",
    category: "distribution",
    subcategory: "Core distribution",
    aliases: [
      "Food Distribution Agreement",
      "Natural Foods Distributor Agreement",
      "Grocery Distributor Agreement",
      "Wholesale Grocery Agreement",
    ],
    stages: ["launch", "distribution", "growth"],
    importance: "essential",
    entityTypes: ["any"],
    parties: "Brand + Food / Grocery Distributor",
    whenYouNeedIt:
      "When a food or natural-channel distributor agrees to carry your SKUs and list you in their catalog. Also when a distributor tells you a retailer will only buy through them, which is common and true more often than founders expect.",
    whatItDoes:
      "Sets the terms on which a distributor buys your product into its warehouses and resells it to retailers and foodservice operators. It covers your price to them, their margin, the allowances and fees deducted along the way, who owns the retailer relationship, and how the relationship ends.",
    whyItMatters:
      "A food distributor takes title, sets its own resale price, and deducts from your invoices before you ever see the money. Founders negotiate the case price and then lose more than that difference to fees they never modelled: catalog listing charges, new-item and warehouse-slotting deductions, promotional funding, spoilage credits, freight allowances and returns. The number that matters is not your list price — it is what actually clears into your bank account per case after everything the distributor takes back.",
    majorTerms: [
      "Price list, price change notice and price protection",
      "Distributor margin or markup structure",
      "Catalog listing and new-item fees",
      "Warehouse slotting or first-order allowances",
      "Promotional allowance funding and deal periods",
      "Freight terms, minimum drop size and freight allowance",
      "Payment terms and early-payment discounts",
      "Spoilage, expired product and unsaleable credit policy",
      "Returns and damaged product handling",
      "Chargeback and deduction procedure",
      "Minimum purchase or velocity requirements",
      "Territory, channel and account coverage",
      "Term, renewal and termination rights",
      "Discontinued SKU and remaining inventory handling",
    ],
    clausesToWatch: [
      {
        clause: "Deduction and chargeback procedure",
        why: "If the distributor can deduct first and document later with no dispute window, you are financing every disagreement and chasing money already gone.",
      },
      {
        clause: "Price protection on price increases",
        why: "A long notice requirement combined with a right to buy ahead at the old price lets a distributor load up before every increase and sell it back to the market against your new pricing.",
      },
      {
        clause: "Unsaleable and spoilage allowance",
        why: "A flat percentage taken off every invoice regardless of actual spoilage is a permanent margin reduction dressed up as a quality policy.",
      },
      {
        clause: "Discontinued item and inventory disposition",
        why: "Without a stated process, discontinuing a SKU can trigger a full return of warehouse stock at your cost, including freight both ways.",
      },
      {
        clause: "Minimum performance or velocity",
        why: "Distributors delist slow items; if the standard is undefined you can be discontinued without notice while still owing catalog and promotional commitments.",
      },
      {
        clause: "Account and channel coverage",
        why: "A grant covering every account in a region can capture chains you sell direct, foodservice you service yourself, and e-commerce you never intended to give away.",
      },
      {
        clause: "Termination and sell-off period",
        why: "A long post-termination sell-off right lets a departing distributor dump inventory into the market at distressed pricing, damaging your shelf price with the retailers you keep.",
      },
    ],
    cpgIssues: [
      "Shelf life and code dating drive the whole economics — distributors typically will not receive product inside a stated portion of its remaining shelf life, and product that ages in their warehouse comes back to you as an unsaleable credit.",
      "Case pack, pallet tie-high, and case dimensions have to fit the distributor's slotting and pick systems; a non-conforming pallet generates handling fees on every receipt.",
      "New-item setup requires a full data package — GTIN, nutrition panel, allergen statement, ingredient deck, certifications, product images — and an incomplete submission delays your listing past the promotional window you already paid for.",
      "Certifications the distributor's retail customers require (organic, non-GMO, kosher, gluten-free) must stay current during the term; a lapsed certificate can pull the item from the catalog.",
      "FIFO and FEFO rotation practice in the distributor's warehouse determines how much of your product ages out; ask what their standard is and get it written down.",
      "Recall and withdrawal cooperation matters here because the distributor holds the lot-level records tying your production runs to specific retail customers.",
      "Freight minimums and drop sizes decide whether small early orders are profitable at all; a below-minimum order can carry a charge larger than the margin on the order.",
      "Distributor margin expectations vary widely by category, channel, region and how much service the distributor provides, so treat any figure you are quoted as that distributor's ask rather than a market rate, and judge it against your own net-per-case model.",
    ],
    founderMistakes: [
      "Modelling the deal on list price minus distributor margin, then finding that catalog fees, promotional funding, freight allowance and spoilage credits took the true net well below their cost per case.",
      "Paying a new-item and promotional package to get listed, then never funding the demo and deal activity needed for velocity, and getting discontinued before the fees had returned anything.",
      "Letting the distributor's grant cover chain accounts the founder was already servicing directly, and paying margin on their own business.",
      "Failing to set a dispute window on deductions, then writing off six figures of unexplained chargebacks as a cost of doing business.",
      "Discontinuing a SKU without checking the inventory disposition clause and receiving a full pallet return, freight collect.",
    ],
    negotiationPoints: [
      "Ask for a stated deduction dispute window, backup documentation on request, and a right to offset validated errors against future invoices.",
      "Ask for spoilage and unsaleable credits to be actual and evidenced rather than a flat percentage off invoice.",
      "Cap the pre-increase buy-ahead quantity so a price increase cannot be neutralised by warehouse loading.",
      "Carve out named chain, e-commerce, foodservice and house accounts you service directly.",
      "Agree in writing how discontinued inventory is handled, including who pays freight and at what price.",
      "Limit the post-termination sell-off period and require the distributor to hold your stated price during it.",
      "Ask for sales-out reporting by retail customer, not just shipment data, at a stated cadence.",
    ],
    redFlags: [
      "A right to deduct any amount at any time with no documentation and no dispute process.",
      "A flat unsaleable or spoilage percentage taken on every invoice regardless of actual returns.",
      "Unlimited buy-ahead ahead of announced price increases.",
      "A grant covering all accounts in the territory with no carve-outs and no account list attached.",
      "No stated process for discontinued SKUs or remaining warehouse inventory.",
      "Open-ended post-termination sell-off with no pricing discipline.",
      "Catalog and setup fees payable up front with no listing commitment or timeline in return.",
    ],
    beforeYouSign: [
      "The distributor's complete current fee and deduction schedule, in writing, including everything not shown on the price list.",
      "Your net-per-case model with every fee, allowance and credit loaded in.",
      "The new-item data package requirements and the actual listing timeline.",
      "The list of retail customers this distributor actually services, and which of them you already sell direct.",
      "Their receiving requirements: minimum remaining shelf life, pallet spec, case pack and appointment rules.",
    ],
    related: [
      "wholesale-distribution-agreement",
      "retail-vendor-agreement",
      "beverage-distributor-agreement",
      "non-exclusive-distribution-agreement",
      "promotional-allowance-agreement",
      "cpg-broker-agreement",
    ],
    attorneyReview: "yes",
    templateId: "food-distributor-agreement",
    templateVersion: "1.0",
    lastReviewed: "2026-08-25",
  },

  {
    id: "dsd-distributor-agreement",
    name: "DSD Distributor Agreement",
    category: "distribution",
    subcategory: "Route and DSD",
    aliases: [
      "Direct Store Delivery Agreement",
      "Route Distribution Agreement",
      "DSD Route Agreement",
      "Direct-Store-Delivery Distributor Agreement",
    ],
    stages: ["distribution", "growth"],
    importance: "common",
    entityTypes: ["any"],
    parties: "Brand + DSD Distributor / Route Operator",
    whenYouNeedIt:
      "When your product needs someone physically in the store — stocking the cold box, rotating code dates, resetting the shelf and taking store-level orders. Typical for chilled beverages, fresh snacks, bakery and anything with a short code.",
    whatItDoes:
      "Appoints a distributor that delivers directly to individual stores on its own trucks and services the shelf, rather than shipping into a retailer's distribution center. It sets the store list, service frequency, merchandising duties, who owns out-of-date product and how routes are handled if the relationship ends.",
    whyItMatters:
      "DSD is the most expensive distribution model per case and the one where service quality decides whether you sell anything. You are paying for a person in the store, so the contract has to buy you that person's actual behaviour: delivery frequency, facings held, cold-box position, rotation of short-coded product. A DSD agreement that grants a territory but never specifies service levels gives away the expensive part of the model while leaving you no way to hold anyone to it.",
    majorTerms: [
      "Store list and route territory definition",
      "Delivery and service frequency per store",
      "Merchandising duties: facings, resets, cold-box placement, rotation",
      "Out-of-date and spoiled product responsibility",
      "Case price to distributor and route margin",
      "Free fill on new store authorizations",
      "Equipment: coolers, racks and POS ownership and placement",
      "Store-level sales and inventory reporting",
      "New account acquisition targets",
      "Term, renewal and termination",
      "Route sale, transfer and assignment rights",
    ],
    clausesToWatch: [
      {
        clause: "Service level definition",
        why: "If frequency, facings and rotation duties are not stated and measurable, you are paying DSD economics for warehouse-level service.",
      },
      {
        clause: "Out-of-date product responsibility",
        why: "On short-coded product this is the largest recurring cost in the model; if it defaults to you with no cap, poor rotation by the distributor becomes your loss.",
      },
      {
        clause: "Route transfer and assignment",
        why: "Routes get bought and sold; without an approval right your brand can end up on a route operated by someone you never evaluated, or by a competitor's distributor.",
      },
      {
        clause: "Equipment ownership and return",
        why: "Coolers and racks you paid for are placed in stores you do not control, and without a return and inventory obligation they simply disappear on termination.",
      },
      {
        clause: "Store list amendment",
        why: "A territory defined by an attached store list needs a stated process for adding stores, or new locations in your area default to nobody.",
      },
      {
        clause: "Termination and store transition",
        why: "Without an obligation to cooperate on transition, a departing DSD operator can leave shelves empty and authorizations lapsed before a replacement is in place.",
      },
    ],
    cpgIssues: [
      "Rotation practice on short-coded product is the whole game — specify FEFO rotation, a minimum remaining code at delivery, and who eats product that dates out on shelf.",
      "Cold chain from the distributor's warehouse through the truck to the cold box needs to be an obligation, not an assumption, for refrigerated beverage and fresh items.",
      "Cold-box position and shelf placement are what you are actually buying with DSD; write the target facings and door position into the service schedule.",
      "Hand-held scan data at the store level is the most useful sell-through data a small brand can get; make reporting of it a contractual obligation rather than a favour.",
      "Free fills on new store authorizations stack up quickly across a route expansion and should be capped per period, not per store only.",
      "Route economics mean a distributor will drop a slow SKU quietly; define a minimum servicing commitment per authorized store so a SKU cannot be dropped without notice.",
    ],
    founderMistakes: [
      "Paying DSD margin for a territory and never writing down a service frequency, then discovering stores were serviced monthly and were out of stock most of the time.",
      "Letting out-of-date credits run uncapped on a short-coded product, so bad rotation by the route driver came straight out of brand margin.",
      "Buying branded coolers and placing them through the distributor with no equipment schedule, then losing track of the assets entirely.",
      "Not securing an approval right over route transfer, and ending up serviced by a route operator whose primary line was a direct competitor.",
      "Relying on the distributor for store-level authorizations without a copy of the retailer paperwork, so leaving the distributor meant losing the shelf.",
    ],
    negotiationPoints: [
      "Write a service schedule with delivery frequency, target facings, rotation standard and reset participation, and tie it to termination for cause.",
      "Cap out-of-date credits and condition them on the distributor meeting the rotation standard.",
      "Require an equipment schedule listing every cooler and rack, with an annual reconciliation and a return obligation on termination.",
      "Ask for store-level sales and inventory data at a stated cadence, in a format you can actually use.",
      "Require your written approval before the route or the agreement is transferred or assigned.",
      "Ask for a transition obligation on termination: continued service for a stated period and cooperation in moving authorizations.",
    ],
    redFlags: [
      "A territory grant with no service frequency, merchandising duties or measurable standard of any kind.",
      "Uncapped out-of-date and unsaleable credits on short-coded product.",
      "Free assignment or route sale without your approval.",
      "No equipment schedule for coolers, racks or displays you paid for.",
      "No obligation to provide store-level data.",
      "Immediate cessation of service on termination with no transition period.",
    ],
    beforeYouSign: [
      "The actual store list with route days, not a territory map.",
      "Your per-case model including route margin, free fills and expected out-of-date credits.",
      "Confirmation of which competing brands ride the same trucks.",
      "The distributor's rotation and cold-chain standard operating procedure, in writing.",
    ],
    related: [
      "beverage-distributor-agreement",
      "territory-distribution-agreement",
      "retail-vendor-agreement",
      "exclusive-distribution-agreement",
      "demo-agreement",
    ],
    attorneyReview: "yes",
    templateId: null,
    templateVersion: null,
    lastReviewed: "2026-08-25",
  },

  {
    id: "wholesale-distribution-agreement",
    name: "Wholesale Distribution Agreement",
    category: "distribution",
    subcategory: "Core distribution",
    aliases: [
      "Wholesale Agreement",
      "Reseller Agreement",
      "Buy-Sell Distribution Agreement",
      "Wholesale Terms",
    ],
    stages: ["launch", "distribution"],
    importance: "common",
    entityTypes: ["any"],
    parties: "Brand + Wholesale Buyer / Distributor",
    whenYouNeedIt:
      "When someone wants to buy your product at wholesale and resell it, and you want standing terms rather than negotiating every purchase order. This is the baseline document behind most early distribution relationships.",
    whatItDoes:
      "Establishes that the other party buys product from you, takes title and resells it on its own account. It fixes pricing, payment, freight, title and risk of loss, ordering mechanics, and what each side may and may not do with the brand.",
    whyItMatters:
      "This is where the distributor-versus-broker distinction actually lands, and founders sign the wrong one all the time. A distributor buys your product, owns the inventory, sets its own resale price and keeps the margin. A broker never takes title — it represents you and earns a commission. Sign a distribution agreement thinking you signed representation and you have given away control of resale pricing, the customer relationship and the receivable, while still believing you have a salesperson.",
    majorTerms: [
      "Wholesale price list and price change notice",
      "Payment terms, credit limit and late payment consequences",
      "Title, risk of loss and delivery terms",
      "Minimum order quantity and minimum order value",
      "Freight responsibility and shipping method",
      "Non-exclusive appointment by default",
      "Permitted resale channels and prohibited channels",
      "Trademark use and marketing approval",
      "Product returns and defect claims window",
      "Term and termination",
      "Insurance and indemnity",
    ],
    clausesToWatch: [
      {
        clause: "Title and risk of loss",
        why: "If risk does not pass at the stated delivery point, you can end up carrying the loss for product damaged on someone else's truck or in someone else's warehouse.",
      },
      {
        clause: "Permitted and prohibited resale channels",
        why: "Silence means a wholesale buyer can list your product on marketplaces at whatever price it likes, undercutting your own site and your retail partners.",
      },
      {
        clause: "Payment terms and credit",
        why: "Extending open terms to a new wholesale account without a credit limit or a stop-ship right means one slow payer can absorb the cash from a whole production run.",
      },
      {
        clause: "Returns and claims window",
        why: "An open-ended right to return unsold product converts what you booked as a sale into a loan of inventory.",
      },
      {
        clause: "Trademark and marketing use",
        why: "Without approval rights, your brand can appear in listings, imagery and claims you would never have made yourself.",
      },
      {
        clause: "Exclusivity by implication",
        why: "A clause promising not to appoint others in an area, buried outside the grant section, creates exclusivity you did not price and did not intend.",
      },
    ],
    cpgIssues: [
      "Marketplace resale by a wholesale buyer is the most common way a brand loses control of its Amazon pricing; address permitted channels explicitly rather than relying on goodwill.",
      "Shelf life at delivery matters: state minimum remaining code at the time of shipment so a buyer cannot claim short-dated stock later.",
      "Storage conditions after title passes should still be specified for temperature-sensitive product, because quality failures reach the consumer with your name on the label.",
      "Lot codes must be recorded by the buyer for traceability, since a withdrawal or recall needs to reach every downstream customer.",
      "Case pack and pallet configuration should be stated so orders arrive in a form the buyer's warehouse accepts without a handling charge.",
      "Product claims and label copy are yours; the agreement should require the buyer to resell product as supplied, unaltered and unrelabelled.",
    ],
    founderMistakes: [
      "Signing a wholesale agreement believing they had hired a salesperson, then finding the buyer owned the retailer relationship and controlled resale pricing entirely.",
      "Shipping a large opening order on open terms with no credit check, and losing both the inventory and the receivable when the buyer folded.",
      "Leaving resale channels unstated, then watching a wholesale account list the product on a marketplace below the brand's own site price.",
      "Accepting a returns clause with no time limit and no restocking condition, and receiving unsold, out-of-code product back a year later.",
      "Agreeing verbally not to sell to anyone else in a region, and having it written into a general covenant they never read as exclusivity.",
    ],
    negotiationPoints: [
      "State clearly that the appointment is non-exclusive unless exclusivity is separately negotiated and paid for with performance commitments.",
      "Set a credit limit, a stop-ship right on overdue balances, and prepayment for opening orders from new accounts.",
      "Define permitted resale channels and expressly address online marketplaces.",
      "Limit returns to defective or non-conforming product within a stated claim window.",
      "Require the buyer to resell product unaltered, in original packaging, within code.",
      "Reserve trademark approval rights for any use of the brand in the buyer's marketing.",
    ],
    redFlags: [
      "Any exclusivity language outside a grant clause you negotiated deliberately.",
      "Unlimited right of return for unsold product.",
      "No credit limit or stop-ship right on a buyer taking open terms.",
      "Silence on resale channels for a product that could end up on a marketplace.",
      "Risk of loss remaining with you after the product leaves your control.",
      "A right to relabel, repack or bundle your product without approval.",
    ],
    beforeYouSign: [
      "A credit reference or trade reference on the buyer, and a decision on opening-order terms.",
      "Your wholesale price list with volume tiers you can actually support.",
      "A written statement of permitted resale channels and pricing expectations.",
      "Confirmation of who pays freight and where title passes.",
    ],
    related: [
      "non-exclusive-distribution-agreement",
      "food-distributor-agreement",
      "wholesale-supply-agreement",
      "purchase-order-terms",
      "broker-distributor-hybrid-agreement",
    ],
    attorneyReview: "yes",
    templateId: null,
    templateVersion: null,
    lastReviewed: "2026-08-25",
  },

  {
    id: "exclusive-distribution-agreement",
    name: "Exclusive Distribution Agreement",
    category: "distribution",
    subcategory: "Exclusivity and territory",
    aliases: [
      "Exclusive Distributorship Agreement",
      "Sole Distribution Agreement",
      "Exclusive Territory Agreement",
    ],
    stages: ["distribution", "growth"],
    importance: "essential",
    entityTypes: ["any"],
    parties: "Brand + Exclusive Distributor",
    whenYouNeedIt:
      "When a distributor asks to be the only one selling your product in a territory, a channel, or an account list — usually in exchange for a promise of investment and focus. Read this before you agree in principle, not after.",
    whatItDoes:
      "Grants one distributor the sole right to sell your product within a defined scope, and in a well-drafted version conditions that right on stated performance: minimum purchases, minimum sales, account coverage and distribution targets, tested on a stated schedule.",
    whyItMatters:
      "Exclusivity is the most valuable thing you have to give and the easiest to give away for free. An exclusive territory with no performance minimums and no clean termination right freezes the region for as long as the agreement runs: you cannot appoint anyone else, you cannot serve those accounts yourself, and the distributor keeps the rights whether or not it sells anything. It also survives you — a buyer acquiring your brand inherits it, and discounts the price for a distributor they cannot exit. Exclusivity should always be rented against performance, never granted outright.",
    majorTerms: [
      "Scope of exclusivity: geography, channel, account list or all three",
      "Whether the grant is exclusive or sole (whether you may still sell direct)",
      "Minimum annual purchase volume",
      "Minimum sales or depletion targets",
      "Points of distribution and account coverage targets",
      "Chain, national and house account carve-outs",
      "E-commerce, Amazon and direct-to-consumer carve-outs",
      "Military, government and institutional carve-outs",
      "Testing period and remedy for missed performance, including conversion to non-exclusive",
      "Term, renewal conditions and notice",
      "Termination for cause, for convenience and cure periods",
      "Sell-off period after termination",
      "Inventory repurchase obligations",
      "Change of control, brand sale and assignment",
    ],
    clausesToWatch: [
      {
        clause: "Performance minimums and the remedy for missing them",
        why: "Minimums with no stated consequence are a forecast, not a condition; the remedy clause is what turns them into leverage.",
      },
      {
        clause: "Exclusive versus sole",
        why: "Exclusive usually means you cannot sell in the territory either; sole means you can. Getting this wrong means you cannot service your own founder-sourced accounts.",
      },
      {
        clause: "Carve-outs from the grant",
        why: "If e-commerce, DTC, Amazon, military and your existing house accounts are not carved out by name, they fall inside the exclusive grant by default.",
      },
      {
        clause: "Termination without cause",
        why: "Without it, ending a failing exclusive relationship requires proving breach, which takes time you do not have while the territory produces nothing.",
      },
      {
        clause: "Cure period mechanics",
        why: "A cure right that can be exercised repeatedly on the same failure lets a distributor cycle through notices indefinitely without ever performing.",
      },
      {
        clause: "Sell-off period",
        why: "A long post-termination right to keep selling inventory lets an ex-distributor compete with your incoming distributor at distressed prices.",
      },
      {
        clause: "Inventory repurchase",
        why: "An obligation to buy back everything at their cost turns your exit decision into a cash-flow decision, and can make terminating unaffordable exactly when you most need to.",
      },
      {
        clause: "Change of control and brand sale",
        why: "Consent rights, payment triggers or termination fees tied to a sale of your brand put the distributor at the table in your own exit and take value out of your price.",
      },
    ],
    cpgIssues: [
      "Performance in CPG should be measured in depletions and points of distribution, not just purchases — a distributor can hit a purchase minimum by loading its own warehouse and still have sold nothing to a retailer.",
      "Account coverage targets should name the actual chains and banners you expect to be opened, because a distributor can meet a raw store count with small independents while never touching the accounts that matter.",
      "Retailer authorizations often sit in the distributor's name; agree in advance who holds the retailer relationship and what happens to authorizations when the agreement ends.",
      "Chain accounts frequently span more than one distributor territory, which is why chains, national accounts and house accounts are carved out and handled centrally.",
      "Shelf life and code dating constrain any inventory repurchase; product returned near end of code has little value to you and should not be repurchased at full cost.",
      "Free fills, samples and promotional funding are the real cost of an exclusive launch; budget them as part of the price of exclusivity, not as extras.",
    ],
    founderMistakes: [
      "Granting exclusivity for a whole region on a verbal promise of focus, with a volume forecast in an appendix that the contract never made binding.",
      "Not distinguishing exclusive from sole, then being unable to sell to a retailer the founder personally opened before the distributor existed.",
      "Failing to carve out e-commerce, and paying distributor margin on their own website sales.",
      "Agreeing to a repurchase obligation covering all inventory at cost, and then being unable to afford to terminate a distributor that had stopped selling.",
      "Learning at the letter-of-intent stage that the distributor had a change-of-control consent right, and giving up purchase price to clear it.",
      "Treating exclusivity as permanent rather than as a term that has to be re-earned at each renewal.",
    ],
    negotiationPoints: [
      "Condition the exclusive grant expressly on stated minimum purchases, minimum depletions and points of distribution, tested annually or quarterly.",
      "Build in conversion to non-exclusive as an automatic remedy for missed performance, so you have a step short of full termination.",
      "Carve out e-commerce, your own DTC channel, Amazon, military and government accounts, and named house and national accounts.",
      "Ask for termination without cause on reasonable notice, and resist a cure right that can be used repeatedly on the same failure.",
      "Limit any inventory repurchase to saleable product within a stated remaining shelf life, at distributor cost, net of amounts owed.",
      "Keep the sell-off period short and require the ex-distributor to hold pricing during it.",
      "Make assignment and change of control free for you, with no consent right, no fee and no termination payment on a brand sale.",
      "Start with a shorter initial term and make renewal conditional on performance rather than automatic.",
    ],
    redFlags: [
      "Exclusivity with no minimum purchase, no minimum sales and no account coverage target.",
      "Performance minimums with no stated remedy anywhere in the agreement.",
      "No termination right other than for uncured material breach.",
      "A consent right, fee or payment triggered by a sale of your brand or your company.",
      "Repurchase of all inventory at cost regardless of age or saleability.",
      "Automatic multi-year renewal with a long advance notice requirement.",
      "An exclusive grant covering all sales in the territory with no carve-outs.",
      "A perpetual term, or a term tied to the distributor continuing to want it.",
    ],
    beforeYouSign: [
      "A performance schedule you would be comfortable enforcing, with the remedy written next to it.",
      "A named list of accounts, channels and platforms you are carving out.",
      "A modelled cost of exclusivity: free fills, samples, promotional funding and the revenue you forgo elsewhere.",
      "The distributor's actual coverage and current account list in the proposed territory.",
      "A clear answer on what happens to retailer authorizations if this ends.",
    ],
    related: [
      "non-exclusive-distribution-agreement",
      "beverage-distributor-agreement",
      "territory-distribution-agreement",
      "master-distributor-agreement",
      "notice-of-termination",
      "brand-acquisition-agreement",
    ],
    attorneyReview: "strongly-recommended",
    attorneyNote:
      "An exclusive grant restricts what you may do with your own product for years and can carry consequences on termination and on a sale of the brand that are difficult to unwind afterwards.",
    templateId: null,
    templateVersion: null,
    lastReviewed: "2026-08-25",
  },

  {
    id: "non-exclusive-distribution-agreement",
    name: "Non-Exclusive Distribution Agreement",
    category: "distribution",
    subcategory: "Exclusivity and territory",
    aliases: [
      "Non-Exclusive Distributorship Agreement",
      "Open Distribution Agreement",
      "Standard Distribution Terms",
    ],
    stages: ["launch", "distribution", "growth"],
    importance: "common",
    entityTypes: ["any"],
    parties: "Brand + Distributor (non-exclusive)",
    whenYouNeedIt:
      "For most first distribution relationships. Use it when a distributor wants to carry your product but has not earned, and has not paid for, the right to be the only one who can.",
    whatItDoes:
      "Appoints a distributor to buy and resell your product without granting any territorial or channel exclusivity. You keep the right to appoint other distributors, to sell direct, and to service your own accounts anywhere.",
    whyItMatters:
      "This is the version most founders should sign first, and the one they most often skip because a distributor asked for exclusivity and the founder did not want to seem difficult. Starting non-exclusive costs you almost nothing: a distributor that performs can earn exclusivity later, on terms you set once you have data. A distributor that does not perform simply stops mattering, instead of holding a region hostage while you have no legal way to replace it.",
    majorTerms: [
      "Express non-exclusive appointment and reservation of rights",
      "Territory or account focus without exclusivity",
      "Price list, price change notice and payment terms",
      "Minimum order quantity and order mechanics",
      "Freight terms and title transfer point",
      "Resale channel rules, including marketplaces",
      "Reporting on sales, accounts and inventory",
      "Path and conditions to earn exclusivity later",
      "Trademark use and marketing approvals",
      "Term, renewal and termination on notice",
      "Post-termination sell-off and account transition",
    ],
    clausesToWatch: [
      {
        clause: "Reservation of rights",
        why: "State affirmatively that you may appoint others and sell direct, or an ambiguous grant can be read as an implied promise of exclusivity.",
      },
      {
        clause: "Most-favoured pricing",
        why: "A promise that no other distributor gets a better price sets your floor across every future negotiation and is exclusivity by another route.",
      },
      {
        clause: "Account registration or protection",
        why: "Registering accounts to one distributor can quietly recreate exclusivity account by account until the territory is effectively closed.",
      },
      {
        clause: "Termination on notice",
        why: "Non-exclusive only helps if you can actually end it; a long notice period or a fixed multi-year term removes the flexibility that made it attractive.",
      },
      {
        clause: "Resale channel and marketplace rules",
        why: "With several distributors buying the same product, uncontrolled marketplace resale drives all of them into a price war that damages your shelf price.",
      },
      {
        clause: "Path to exclusivity",
        why: "An undefined promise that exclusivity comes later creates an expectation you will be arguing about, rather than a performance test you can measure.",
      },
    ],
    cpgIssues: [
      "Multiple distributors buying the same SKUs means channel conflict is a pricing question — publish a consistent wholesale price structure with volume tiers so the same case does not reach a retailer at two very different costs.",
      "Overlapping distributors can both call on the same chain buyer, which confuses the retailer and damages your credibility; a light-touch account registration with an expiry date handles this without granting exclusivity.",
      "Code dating gets worse with more inventory in more warehouses; set a minimum remaining shelf life at delivery and monitor how much stock sits across the network.",
      "Promotional funding must be allocated on a stated basis when several distributors are asking for deal support, or the loudest one absorbs the budget.",
      "Transshipping between distributors undercuts your pricing structure; address whether a distributor may sell to another distributor at all.",
      "Traceability duties still apply to every distributor regardless of exclusivity, so lot-code recording and recall cooperation belong in every version.",
    ],
    founderMistakes: [
      "Granting exclusivity to the first distributor who asked, when a non-exclusive appointment would have produced the same effort and left the region open.",
      "Writing non-exclusive on the front page and then adding a covenant not to appoint anyone else in the region, which recreated exclusivity in the back half.",
      "Promising a best-price commitment to an early distributor and then being unable to build a volume-tiered price list later.",
      "Never defining a path to exclusivity, then facing an angry distributor who believed it had been earned.",
      "Failing to set marketplace rules across several distributors, and watching their online listings collapse the brand's price.",
    ],
    negotiationPoints: [
      "Say expressly that the appointment is non-exclusive and reserve the right to appoint others and to sell direct.",
      "Offer a defined performance path to exclusivity instead of exclusivity itself: hit these volumes and accounts for this many periods, then we talk.",
      "Keep the term short with termination on reasonable notice for either side.",
      "Publish a volume-tiered price list rather than granting a most-favoured-price promise.",
      "Set marketplace and transshipping rules that apply consistently across every distributor.",
      "Ask for sales and account reporting so you can compare distributors on evidence when exclusivity comes up.",
    ],
    redFlags: [
      "Any covenant restricting who else you may appoint, anywhere in the document.",
      "A most-favoured-nation or best-price commitment.",
      "Open-ended account registration with no expiry and no performance test.",
      "A multi-year term with no termination on notice.",
      "Silence on marketplace resale where multiple distributors carry the same SKUs.",
      "A vague written promise that exclusivity will follow.",
    ],
    beforeYouSign: [
      "A wholesale price list with volume tiers you can defend across every distributor.",
      "A written statement of your marketplace and resale channel rules.",
      "The performance metrics you would want to see before ever discussing exclusivity.",
    ],
    related: [
      "exclusive-distribution-agreement",
      "wholesale-distribution-agreement",
      "food-distributor-agreement",
      "territory-distribution-agreement",
      "beverage-distributor-agreement",
    ],
    attorneyReview: "yes",
    templateId: "non-exclusive-distribution-agreement",
    templateVersion: "1.0",
    lastReviewed: "2026-08-25",
  },

  {
    id: "territory-distribution-agreement",
    name: "Territory Distribution Agreement",
    category: "distribution",
    subcategory: "Exclusivity and territory",
    aliases: [
      "Territorial Distribution Agreement",
      "Regional Distribution Agreement",
      "Area Distributor Agreement",
    ],
    stages: ["distribution", "growth"],
    importance: "situational",
    entityTypes: ["any"],
    parties: "Brand + Regional Distributor",
    whenYouNeedIt:
      "When you are dividing the country between several distributors and need each one's boundaries written precisely enough that they do not overlap, leak or trap accounts you meant to keep.",
    whatItDoes:
      "Defines the scope of a distributor's appointment by geography, channel or account list, and sets the rules for what happens at the edges: transshipping, chain accounts that cross lines, boundary changes and the addition or removal of accounts.",
    whyItMatters:
      "Territory is where distribution agreements are actually won or lost, because a poorly drawn territory is what makes an otherwise reasonable contract impossible to manage. Territory defined only as a state name sweeps in every chain headquarters, e-commerce order and military base inside the line. Once several distributors are in place, an ambiguous boundary means two of them calling the same buyer, or a valuable account belonging to nobody while both invoice you for it.",
    majorTerms: [
      "Territory definition: geography, channel, account list or a combination",
      "Named account inclusions and exclusions",
      "Chain and national account handling across territory lines",
      "House account reservation",
      "E-commerce and shipped-order attribution",
      "Transshipping and sales outside the territory",
      "Process for adding, removing or splitting territory",
      "Performance measured per territory",
      "Pricing consistency across adjacent territories",
      "Term, renewal and termination",
    ],
    clausesToWatch: [
      {
        clause: "Territory definition method",
        why: "A territory defined by a state name behaves very differently from one defined by an account list, and mixing the two without saying which governs creates a dispute at the first chain account.",
      },
      {
        clause: "Chain and national account treatment",
        why: "If a chain with stores in three territories is not handled centrally, you can owe margin to three distributors on the same purchase order.",
      },
      {
        clause: "Transshipping",
        why: "A distributor selling into a neighbour's territory at a lower price collapses your pricing across both and puts you in the middle of a fight between two partners.",
      },
      {
        clause: "Territory reduction as a remedy",
        why: "Without an express right to shrink or split an underperforming territory, your only options are to tolerate it or to terminate entirely.",
      },
      {
        clause: "E-commerce and shipped orders",
        why: "An order placed online and shipped to a consumer inside a territory can be claimed by that distributor unless the agreement says online sales sit outside the grant.",
      },
      {
        clause: "Boundary amendment process",
        why: "Without a stated process, adding a new territory or moving a line requires renegotiating every agreement at once, from a weak position.",
      },
    ],
    cpgIssues: [
      "Chain accounts almost never respect distributor boundaries — grocery, club and convenience banners buy centrally and ship to their own DCs, so they need to be treated as house or national accounts rather than assigned geographically.",
      "Military commissaries and exchanges and government institutional accounts buy on their own systems and should be carved out by name rather than swept in by geography.",
      "Foodservice and on-premise distribution rarely follows the same lines as retail, so a single geographic grant can accidentally cover a channel the distributor cannot service.",
      "Distributor DC locations, not state lines, determine what a distributor can actually deliver economically; define territory to match the freight reality.",
      "Consistent pricing across adjacent territories is what prevents diverted product from appearing at a lower shelf price two states over.",
      "Retailer authorizations obtained by one distributor may not transfer when a territory is split, so plan for who holds the paperwork before you redraw a line.",
    ],
    founderMistakes: [
      "Defining a territory as an entire state, and discovering that the state contained a national chain headquarters that the founder had been servicing directly.",
      "Granting overlapping territories to two distributors who then called on the same buyer in the same week with different prices.",
      "Leaving e-commerce unaddressed, then receiving margin claims from three distributors on the same online order volume.",
      "Having no right to reduce a territory, so a distributor that performed well in one metro held five that it never worked.",
      "Ignoring transshipping until a distributor sold containers of product into a neighbouring region at below the local price.",
    ],
    negotiationPoints: [
      "Define territory by an explicit method and attach the account list, banner list or geography as a schedule you can amend.",
      "Reserve chain, national, house, military, government and e-commerce accounts centrally and name them.",
      "Include an express right to reduce or split territory for underperformance, as a remedy short of termination.",
      "Prohibit active solicitation and sales outside the territory, and address transshipping to other distributors directly.",
      "Set a stated process and notice period for boundary amendments.",
      "Require per-territory performance reporting so you can compare regions on the same basis.",
    ],
    redFlags: [
      "Territory described only as a state or region with no account schedule and no carve-outs.",
      "No treatment of chain or national accounts that cross territory lines.",
      "No mechanism to change, reduce or split a territory for any reason.",
      "Silence on e-commerce order attribution.",
      "A right to sell anywhere with no restriction on transshipping.",
      "Territory granted in perpetuity or for the life of the brand.",
    ],
    beforeYouSign: [
      "A map or schedule that you and the distributor have both marked up and agree on.",
      "A named list of house, chain, national, military and e-commerce accounts sitting outside every territory.",
      "Your pricing structure across adjacent territories, checked for gaps a diverter could exploit.",
    ],
    related: [
      "exclusive-distribution-agreement",
      "non-exclusive-distribution-agreement",
      "master-distributor-agreement",
      "dsd-distributor-agreement",
      "international-distributor-agreement",
    ],
    attorneyReview: "yes",
    templateId: null,
    templateVersion: null,
    lastReviewed: "2026-08-25",
  },

  {
    id: "master-distributor-agreement",
    name: "Master Distributor Agreement",
    category: "distribution",
    subcategory: "Multi-tier distribution",
    aliases: [
      "Master Distributorship Agreement",
      "National Master Distributor Agreement",
      "Primary Distributor Agreement",
    ],
    stages: ["distribution", "growth"],
    importance: "situational",
    entityTypes: ["any"],
    parties: "Brand + Master Distributor",
    whenYouNeedIt:
      "When one distributor proposes to handle a large region or the whole country and to appoint sub-distributors underneath itself, rather than you managing a dozen relationships directly.",
    whatItDoes:
      "Appoints a single distributor at the top of a multi-tier network, with the right to sell to sub-distributors it appoints. It sets the terms flowing down to that second tier, the master's responsibility for its sub-distributors, and what happens to the network if the master relationship ends.",
    whyItMatters:
      "A master distributor structure buys you simplicity and costs you visibility and margin. Two layers of markup sit between your price and the retail shelf, and you generally cannot see the sub-distributor's price, its accounts or its performance. If the master relationship ends, the entire second tier can go with it, because your contract is with the master and not with anyone actually calling on stores. The protections that matter are flow-down obligations, direct reporting and a right to step in.",
    majorTerms: [
      "Scope: territory, channels and whether exclusive",
      "Right to appoint sub-distributors and your approval rights",
      "Required flow-down terms in sub-distributor agreements",
      "Master's responsibility for sub-distributor acts and payment",
      "Margin structure across both tiers and resale price discipline",
      "Aggregate and per-tier performance requirements",
      "Reporting: sub-distributor sales, accounts and inventory",
      "Brand's direct access to sub-distributors and retail accounts",
      "Step-in and direct appointment rights on termination",
      "Term, renewal and termination",
      "Assignment and change of control",
    ],
    clausesToWatch: [
      {
        clause: "Sub-distributor approval",
        why: "Without an approval right your product can be handed to operators you have never assessed, including ones already carrying a direct competitor.",
      },
      {
        clause: "Flow-down obligations",
        why: "If your quality, traceability, trademark and channel rules do not flow down in writing, they simply do not apply one level below the master.",
      },
      {
        clause: "Master's responsibility for sub-distributors",
        why: "If the master is not answerable for its network's performance and payment, you carry the risk of a tier you cannot see and cannot control.",
      },
      {
        clause: "Step-in rights on termination",
        why: "Without a right to contract directly with sub-distributors when the master agreement ends, terminating the master can wipe out your entire route to market at once.",
      },
      {
        clause: "Margin stacking and resale pricing",
        why: "Two markups can push the shelf price above what the category supports, and the brand usually finds out only after velocity disappoints.",
      },
      {
        clause: "Reporting through the tiers",
        why: "Aggregate shipment data from the master tells you nothing about which accounts are actually selling, leaving you blind when you need to make decisions.",
      },
    ],
    cpgIssues: [
      "Traceability has to survive two hops — lot-level records must be kept by both tiers or a withdrawal cannot reach the retail customers actually holding your product.",
      "Two layers of warehousing means product ages longer before it reaches shelf; set minimum remaining shelf life at each transfer, not only at your shipment.",
      "Retailer authorizations are usually held at the sub-distributor level, which is exactly the tier you have no contract with; secure a right to those relationships.",
      "Promotional funding paid to the master frequently does not reach the store level; require proof of performance for any allowance passed down.",
      "Certification and label compliance obligations, including approved claims and artwork versions, need to bind sub-distributors explicitly.",
      "Chain accounts should be reserved centrally, because a master distributor and one of its subs can otherwise both work the same banner.",
    ],
    founderMistakes: [
      "Appointing a master distributor for the whole country with no approval right over sub-distributors, and learning later that a key metro had been handed to a competitor's wholesaler.",
      "Receiving only shipment data from the master, and having no idea which accounts were selling until velocity reports came back from a retailer.",
      "Terminating a master distributor and losing every downstream account at the same time, because there was no direct relationship with the second tier.",
      "Funding a national promotional program through the master that never reached store level, with no proof-of-performance requirement to point to.",
      "Not noticing that stacked margins had pushed shelf price above the category, then blaming the marketing budget for weak velocity.",
    ],
    negotiationPoints: [
      "Require written approval before any sub-distributor is appointed, and a current list of the network at all times.",
      "Attach the required flow-down terms as a schedule and make the master responsible for including them.",
      "Ask for sub-distributor-level sales, account and inventory reporting, not just master-level shipments.",
      "Negotiate a step-in right allowing you to contract directly with sub-distributors on termination, and to be introduced to them during the term.",
      "Agree a target shelf price range and a margin structure across both tiers that supports it.",
      "Require proof of performance for promotional funds before they are earned.",
      "Reserve chain, national and house accounts to yourself, outside the master grant.",
    ],
    redFlags: [
      "An unrestricted right to appoint sub-distributors with no approval and no disclosure.",
      "No flow-down of quality, traceability, trademark or channel obligations.",
      "No obligation on the master for sub-distributor payment or performance.",
      "No right to communicate with sub-distributors or retail accounts.",
      "No step-in or direct appointment right on termination.",
      "Exclusive national appointment with no aggregate performance requirement.",
    ],
    beforeYouSign: [
      "The proposed sub-distributor network by region, with the competing brands each of them carries.",
      "A modelled shelf price with both margins stacked in, checked against the category.",
      "The reporting package you will receive, at both tiers, in writing.",
      "A written plan for what happens to the network if this agreement ends.",
    ],
    related: [
      "sub-distributor-agreement",
      "exclusive-distribution-agreement",
      "territory-distribution-agreement",
      "international-distributor-agreement",
      "beverage-distributor-agreement",
    ],
    attorneyReview: "yes",
    templateId: null,
    templateVersion: null,
    lastReviewed: "2026-08-25",
  },

  {
    id: "sub-distributor-agreement",
    name: "Sub-Distributor Agreement",
    category: "distribution",
    subcategory: "Multi-tier distribution",
    aliases: [
      "Secondary Distributor Agreement",
      "Sub-Distributorship Agreement",
      "Appointed Sub-Distributor Terms",
    ],
    stages: ["distribution", "growth"],
    importance: "situational",
    entityTypes: ["any"],
    parties: "Master Distributor (or Brand) + Sub-Distributor",
    whenYouNeedIt:
      "When a master or regional distributor appoints someone beneath it to service part of a territory, and you want the brand's terms to reach that second tier rather than stopping one level up.",
    whatItDoes:
      "Sets the terms for the second tier of a distribution network: what the sub-distributor may sell, where, at what price, and which of the brand's obligations bind it directly. In the better version, it also gives the brand rights it can enforce even though it is not the counterparty.",
    whyItMatters:
      "Sub-distributors are usually the ones actually calling on stores, holding the retailer authorizations and touching your product last — and they are usually the tier a brand has no contract with. If the master agreement ends and there is nothing binding the sub-distributor to you, your shelf presence can disappear with a relationship you were not even part of. The value of this document is that it makes the tier that does the work reachable.",
    majorTerms: [
      "Scope of appointment and defined sub-territory",
      "Flow-down of the brand's quality, traceability and trademark terms",
      "Resale pricing discipline and permitted channels",
      "Reporting obligations owed to the master and to the brand",
      "Performance expectations at the sub-territory level",
      "Brand's third-party rights and direct enforcement",
      "What happens if the master agreement terminates",
      "Term, termination and notice",
      "Inventory disposition on termination",
    ],
    clausesToWatch: [
      {
        clause: "Survival on termination of the master agreement",
        why: "If the sub-distributor agreement dies automatically with the master's, ending one relationship silently ends every relationship below it.",
      },
      {
        clause: "Brand third-party rights",
        why: "Without an express right to enforce, the brand cannot act on a quality, trademark or channel breach by the tier actually handling its product.",
      },
      {
        clause: "Flow-down completeness",
        why: "Terms that were negotiated hard at the master level are worthless if a simplified sub-agreement drops them.",
      },
      {
        clause: "Retailer authorization ownership",
        why: "Authorizations held in the sub-distributor's name can be lost entirely when that relationship ends, taking the shelf with them.",
      },
      {
        clause: "Resale channel limits",
        why: "A sub-distributor listing product on an online marketplace can undercut the whole network's pricing from a tier you do not directly control.",
      },
      {
        clause: "Reporting to the brand",
        why: "If reporting runs only to the master, the brand never sees account-level performance and cannot make network decisions on evidence.",
      },
    ],
    cpgIssues: [
      "Lot-code records must be kept at this tier because it is the last point before retail, and a withdrawal that cannot reach store level is not a withdrawal.",
      "Storage and cold-chain obligations belong here, since this is the party physically holding and moving the product before it reaches the shelf.",
      "Minimum remaining shelf life at receipt should be stated at this tier as well as the tier above, or aged stock accumulates exactly where you can least see it.",
      "Approved label and artwork versions and permitted product claims need to bind this tier, because it is the one supplying retailers directly.",
      "Free fills, samples and demo product supplied through the network need to be accounted for at this level or they disappear without any record.",
      "Retail authorizations, planogram positions and reset participation typically sit here, so the paperwork trail should be visible to the brand.",
    ],
    founderMistakes: [
      "Assuming that terms agreed with a master distributor automatically bound everyone beneath it, and finding that none of the quality or channel terms had flowed down.",
      "Never meeting the sub-distributors that held every retailer relationship, and losing the accounts when the master relationship ended.",
      "Having no reporting from the tier doing the selling, and making network decisions from shipment data that reflected warehouse loading.",
      "Leaving marketplace resale unaddressed at this tier, so one sub-distributor's online listing set the price for the entire network.",
    ],
    negotiationPoints: [
      "Require the master to use a form of sub-distributor agreement you have approved, attached as a schedule.",
      "Ask for express third-party enforcement rights for the brand on quality, traceability, trademark and channel terms.",
      "Provide for the sub-distributor agreement to survive, or convert to a direct agreement with the brand, if the master agreement ends.",
      "Require reporting to run to the brand as well as to the master.",
      "Address marketplace and transshipping rules at this tier explicitly.",
      "Have retailer authorization records shared with the brand as they are obtained.",
    ],
    redFlags: [
      "Automatic termination of the sub-agreement whenever the master agreement ends, with no conversion right.",
      "No flow-down of quality, recall cooperation or trademark obligations.",
      "No brand visibility or enforcement rights of any kind.",
      "Sub-distributor free to set any resale price in any channel.",
      "No obligation to record lot codes or cooperate in a withdrawal.",
    ],
    beforeYouSign: [
      "The form of sub-distributor agreement the master actually uses, not a description of it.",
      "A current list of appointed sub-distributors and the territories they hold.",
      "A written answer on who holds retailer authorizations in each sub-territory.",
    ],
    related: [
      "master-distributor-agreement",
      "territory-distribution-agreement",
      "exclusive-distribution-agreement",
      "wholesale-distribution-agreement",
      "quality-agreement",
    ],
    attorneyReview: "yes",
    templateId: null,
    templateVersion: null,
    lastReviewed: "2026-08-25",
  },

  {
    id: "international-distributor-agreement",
    name: "International Distributor Agreement",
    category: "distribution",
    subcategory: "Cross-border distribution",
    aliases: [
      "Export Distribution Agreement",
      "Overseas Distributor Agreement",
      "Foreign Distributor Agreement",
    ],
    stages: ["growth", "distribution"],
    importance: "advanced",
    entityTypes: ["any"],
    parties: "Brand + Distributor in another country",
    whenYouNeedIt:
      "When a distributor outside your home country wants to import and sell your product. Often this starts with an unsolicited inbound enquiry and a request for exclusive national rights, which is exactly when to slow down.",
    whatItDoes:
      "Appoints a distributor to import, clear customs on and resell your product in another country. It has to handle everything a domestic agreement does, plus import compliance, local labelling, trademark protection in that country, currency, shipping terms and which country's law and forum govern a dispute.",
    whyItMatters:
      "Cross-border distribution puts your brand in a market where you have little visibility and limited practical leverage. Several countries have their own regimes governing how distributors and agents may be terminated and what compensation may be owed, and those rules vary a great deal — this needs local counsel in the specific country before signing, not after a dispute. Founders also routinely discover that their distributor registered the trademark locally in its own name, which converts a sales partner into the owner of your brand in that market.",
    majorTerms: [
      "Country or region of appointment and whether exclusive",
      "Performance minimums measured in local terms",
      "Trademark registration and ownership in the local market",
      "Import, customs and regulatory registration responsibility",
      "Local labelling, language and ingredient compliance",
      "Incoterms, title transfer and shipping responsibility",
      "Currency, pricing and payment security",
      "Parallel import and re-export restrictions",
      "Term, renewal and termination mechanics",
      "Governing law, forum and dispute resolution",
      "Post-termination inventory and trademark cleanup",
    ],
    clausesToWatch: [
      {
        clause: "Trademark registration in the local market",
        why: "A distributor that registers your mark in its own name can control your ability to sell in that country long after the relationship ends.",
      },
      {
        clause: "Termination and notice",
        why: "Termination rights and any compensation on exit can be affected by local rules that differ from your home market, so this needs local counsel before signature rather than after notice.",
      },
      {
        clause: "Regulatory registration ownership",
        why: "If import approvals or product registrations sit in the distributor's name, changing distributors can mean re-registering the product from scratch.",
      },
      {
        clause: "Governing law and forum",
        why: "A judgment you cannot practically enforce where the distributor's assets are is close to no remedy at all.",
      },
      {
        clause: "Payment security",
        why: "Extending open credit across a border to a new counterparty means the inventory and the receivable can both be gone before you have any warning.",
      },
      {
        clause: "Parallel import and re-export",
        why: "Product priced for one market flowing back into another undercuts your domestic pricing and your other distributors.",
      },
      {
        clause: "Label and claims compliance",
        why: "Local labelling, language and permitted-claim rules differ, and an unclear allocation of responsibility can leave stock stranded at the border.",
      },
    ],
    cpgIssues: [
      "Ingredient and additive rules differ between markets, so a formula that is fine at home may need reformulation before it can be imported at all — settle who pays for that work.",
      "Local-language labelling, net-content declarations and nutrition formats usually require a different label version; decide who produces it, who approves the artwork and who owns the file.",
      "Shelf life on ocean freight is consumed before the product ever reaches a local warehouse, so set minimum remaining code at arrival, not at departure.",
      "Certificates of analysis, health certificates and free-sale certificates are commonly required for import clearance; agree who obtains them and who pays.",
      "Product registration with the destination market's authorities can take months and should be started before you commit to a launch date.",
      "Cold chain across ocean or air freight is a real failure point for temperature-sensitive product; specify container type, monitoring and who bears loss.",
      "Permitted claims vary by market, so marketing copy approved at home may need to change, and approval rights over local marketing matter more than usual.",
    ],
    founderMistakes: [
      "Granting exclusive national rights to an inbound enquiry with no performance minimums, and closing an entire country for years on a handful of container orders.",
      "Letting the distributor register the trademark locally as a convenience, then having to buy the brand back to change partners.",
      "Allowing product registrations and import approvals to be held in the distributor's name, making a change of distributor a full restart.",
      "Shipping on open terms without a letter of credit or prepayment, and losing both the container and the payment.",
      "Never addressing re-export, and watching product priced for an export market reappear in the home market below list.",
    ],
    negotiationPoints: [
      "Require that you own, or immediately receive assignment of, any local trademark registration, and that the distributor will not file in its own name.",
      "Keep product registrations and import approvals in your name where the local system allows, and require cooperation to transfer them if it does not.",
      "Set performance minimums in local units with an express remedy, and prefer a shorter initial term with renewal on performance.",
      "Use prepayment, a letter of credit or another payment security for early orders.",
      "State Incoterms precisely and confirm who bears the cost and risk at each stage.",
      "Prohibit re-export and sales outside the appointed country, and address parallel imports directly.",
      "Get local counsel in the destination country on termination and any exit compensation before signing.",
    ],
    redFlags: [
      "Exclusive rights to an entire country with no minimums and no termination for convenience.",
      "The distributor holding, or being permitted to file, the trademark locally.",
      "Product registrations or import licences held in the distributor's name with no transfer obligation.",
      "Open credit terms to a new cross-border counterparty with no security.",
      "No allocation of responsibility for local labelling and regulatory compliance.",
      "Governing law and forum in a place where you could not practically pursue a remedy.",
      "Automatic renewal with a long notice period on a market you cannot monitor.",
    ],
    beforeYouSign: [
      "Local counsel in the destination country on termination, agent and distributor protection, and trademark filing.",
      "A trademark search and a filing plan for the market, in your own name.",
      "A written list of the regulatory registrations and import documents the product needs and who will hold them.",
      "The distributor's trade references, import history and existing brand portfolio.",
      "Your landed-cost model including freight, duty, local compliance and the distributor's margin.",
    ],
    related: [
      "importer-agreement",
      "international-sales-agent-agreement",
      "international-trademark-license-agreement",
      "export-agent-agreement",
      "exclusive-distribution-agreement",
      "master-distributor-agreement",
    ],
    attorneyReview: "strongly-recommended",
    attorneyNote:
      "Termination rules, distributor protection regimes, trademark filing practice and import requirements differ by country, and local counsel in the destination market is the only reliable way to know what you are agreeing to.",
    templateId: null,
    templateVersion: null,
    lastReviewed: "2026-08-25",
  },

  {
    id: "broker-distributor-hybrid-agreement",
    name: "Broker / Distributor Hybrid Agreement",
    category: "distribution",
    subcategory: "Hybrid arrangements",
    aliases: [
      "Hybrid Broker Distributor Agreement",
      "Combined Broker and Distribution Agreement",
      "Sales and Distribution Agreement",
    ],
    stages: ["distribution", "growth"],
    importance: "situational",
    entityTypes: ["any"],
    parties: "Brand + Partner acting as both broker and distributor",
    whenYouNeedIt:
      "When one company wants to both represent you on commission in some accounts and buy and resell your product in others. Common when a regional distributor also has a broker arm, or when a broker offers to warehouse and ship for smaller accounts.",
    whatItDoes:
      "Separates two fundamentally different relationships inside one contract: the accounts where the partner acts as your agent for a commission and never takes title, and the accounts where it buys product outright and resells on its own margin. It defines which accounts fall in which bucket and how each is paid.",
    whyItMatters:
      "Founders confuse distributors and brokers constantly, and this document is where the confusion becomes expensive. A distributor buys your product, owns the inventory, sets the resale price and keeps the margin; a broker never takes title, does not own inventory, and earns a commission on sales it produces for you. Blend them without drawing the line and you can end up paying a commission on an account where the same partner already took a distributor margin, or losing the customer relationship in accounts you thought you still owned.",
    majorTerms: [
      "Account schedule assigning each account to broker or distributor treatment",
      "Commission rate and basis for brokered accounts",
      "Wholesale price and margin for distributed accounts",
      "Express prohibition on earning both on the same sale",
      "Who owns the customer relationship in each bucket",
      "Title, inventory ownership and credit risk by bucket",
      "House account definition and reservation",
      "Reporting requirements for each type of account",
      "Post-termination commission tail on brokered accounts",
      "Inventory handling on termination for distributed accounts",
      "Term, termination and notice",
    ],
    clausesToWatch: [
      {
        clause: "Account classification and how it changes",
        why: "If accounts can move between brokered and distributed without your consent, the partner can pick whichever treatment pays it more on any given order.",
      },
      {
        clause: "Double compensation",
        why: "Without an express bar, the same partner can take a distributor margin on the case and a commission on the sale of that same case.",
      },
      {
        clause: "Customer relationship ownership",
        why: "In brokered accounts the customer should be yours; if the agreement is silent, the partner can treat your accounts as its own book on exit.",
      },
      {
        clause: "Credit risk allocation",
        why: "Brokered sales leave the receivable with you and distributed sales move it to the partner, so an unclear split means you may be carrying credit risk you thought you had passed on.",
      },
      {
        clause: "Post-termination commission tail",
        why: "An open-ended tail on brokered accounts means you keep paying commission long after the partner has stopped doing anything.",
      },
      {
        clause: "Inventory on termination",
        why: "The distributed side of the relationship carries a repurchase question that the brokered side does not; leaving it unaddressed makes exit costs unpredictable.",
      },
    ],
    cpgIssues: [
      "Retailer authorizations obtained on brokered accounts should be recorded in your name, since you are the vendor of record and the relationship is supposed to be yours.",
      "Deductions and chargebacks land differently: on distributed accounts the partner absorbs them into its margin, while on brokered accounts they hit your invoice directly, so the deduction policy has to say which is which.",
      "Free fills and demo product supplied into brokered accounts are your cost and should be tracked separately from product the partner bought.",
      "Spoils and unsaleable credits on distributed inventory belong to the partner once title passes; on brokered accounts they come back to you, so a single blended spoils clause will be wrong for half the business.",
      "Promotional funding needs separate treatment because on brokered accounts you are funding the retailer directly and on distributed accounts you may be funding the partner's own margin.",
      "Lot traceability differs: on brokered sales product ships from your warehouse or 3PL to the retailer, so your own records carry the trail, while distributed volume disappears into the partner's warehouse.",
    ],
    founderMistakes: [
      "Signing what they believed was a broker agreement and discovering it was a distribution agreement, with the partner owning the inventory, the pricing and the retailer relationship.",
      "Paying a commission on accounts where the same partner had already taken a distributor margin, because nothing barred double compensation.",
      "Never attaching an account schedule, so every quarter became an argument about which accounts were brokered and which were bought.",
      "Assuming credit risk had passed to the partner across the whole relationship, and absorbing retailer bad debt on the brokered half.",
      "Accepting a commission tail that applied to accounts the partner had never opened, and paying it for years after termination.",
    ],
    negotiationPoints: [
      "Attach an account schedule, classify every account as brokered or distributed, and require written agreement to move any account between the two.",
      "State expressly that no sale can generate both a commission and a distributor margin.",
      "Confirm in writing that in brokered accounts you are the vendor of record and the customer relationship is yours.",
      "Set separate deduction, spoils and promotional treatment for each bucket rather than one blended clause.",
      "Limit any post-termination commission tail to accounts the partner actually opened, for a defined and short period.",
      "Define house accounts you service yourself and exclude them from both buckets.",
    ],
    redFlags: [
      "One undifferentiated compensation clause covering both brokered and distributed volume.",
      "No account schedule, or a schedule the partner can amend unilaterally.",
      "No express prohibition on double compensation.",
      "The partner named as the vendor of record on accounts you believed were yours.",
      "An indefinite post-termination commission tail.",
      "Silence on who bears retailer deductions and bad debt in each bucket.",
    ],
    beforeYouSign: [
      "A completed account schedule, agreed line by line, showing which accounts sit in which bucket.",
      "A clear written statement of who is the vendor of record in each bucket.",
      "Separate margin models for the brokered and distributed sides of the relationship.",
      "The house accounts you are reserving, listed by name.",
    ],
    related: [
      "broker-plus-distributor-agreement",
      "cpg-broker-agreement",
      "wholesale-distribution-agreement",
      "commission-sales-agreement",
      "food-distributor-agreement",
    ],
    attorneyReview: "yes",
    templateId: null,
    templateVersion: null,
    lastReviewed: "2026-08-25",
  },
];

export default DISTRIBUTION_DOCS;
